import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Building2, Shield, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmpresaLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginEmpresa, user } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");

  useEffect(() => {
    fetchEmpresa();
  }, [slug]);

  useEffect(() => {
    if (user) {
      navigate(`/${slug}/admin`);
    }
  }, [user, slug, navigate]);

  const fetchEmpresa = async () => {
    try {
      const res = await axios.get(`${API_URL}/empresas/${slug}/check`);
      if (res.data.exists) {
        setEmpresa(res.data);
      } else {
        navigate("/");
        toast.error(t("common.error"));
      }
    } catch (error) {
      navigate("/");
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await loginEmpresa(slug, email, senha);
      toast.success(t("common.success"));
      navigate(`/${slug}/admin`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("login.invalidCredentials"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-4">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{empresa?.nome ? `Login Administrativo - ${empresa.nome}` : 'Login Administrativo - OmbudNow'}</title>
        <meta name="description" content={`Acesse o painel administrativo do canal de ética da ${empresa?.nome || 'empresa'}. Gerencie denúncias e ouvidoria com segurança.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link
          to={`/${slug}`}
          className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("login.backToChannel")}
        </Link>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#1a1a2e] px-6 py-8 text-center">
            {empresa?.logo_url ? (
              <img
                src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)}
                alt={empresa.nome}
                className="h-12 mx-auto mb-4"
              />
            ) : (
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
            <h1 className="text-xl font-bold text-white">{empresa?.nome}</h1>
            <p className="text-sm text-slate-400 mt-1">{t("login.subtitle")}</p>
          </div>

          {/* Form */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ex. your@email.com"
                  required
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="senha">{t("login.password")}</Label>
                <Input
                  id="senha"
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="input-senha"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1a1a2e] hover:bg-[#2d2d4a]"
                disabled={submitting}
                data-testid="btn-login"
              >
                {submitting ? t("login.loggingIn") : t("login.loginButton")}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-white/50 mt-8">
          Powered by <span className="font-semibold">Ombudnow</span>
        </p>
      </div>
    </div>
  );
}
